import React, { useState, useEffect } from 'react';
import { X, CalendarDays, ChevronRight } from 'lucide-react';
import {
  ProgramacionService,
  type ProgramacionSemanal,
  type ProgramacionAsignacion,
} from '../../services/programacion/programacion.service';
import { AsignacionDetailsDrawer } from './AsignacionDetailsDrawer';
import { EstadoSeguimientoBadge } from './EstadoSeguimientoBadge';
import { parseFechaLocal } from '../../utils/date.utils';

interface ProgramacionDetailsDrawerProps {
  isOpen: boolean;
  programacion: ProgramacionSemanal | null; // debe traer asignaciones (cargada por ID)
  onClose: () => void;
  onEdit: (programacion: ProgramacionSemanal) => void;
}

const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const formatearFecha = (iso: string): string => {
  const d = parseFechaLocal(iso);
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};

const diaDeFecha = (iso: string): string => {
  const d = parseFechaLocal(iso);
  return DIAS_SEMANA[d.getDay()] ?? '';
};

export const ProgramacionDetailsDrawer: React.FC<ProgramacionDetailsDrawerProps> = ({
  isOpen,
  programacion,
  onClose,
  onEdit,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [full, setFull] = useState<ProgramacionSemanal | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [asignacionSel, setAsignacionSel] = useState<ProgramacionAsignacion | null>(null);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    }
    setIsVisible(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !programacion) return;
    setIsLoading(true);
    ProgramacionService.getById(programacion.id)
      .then(setFull)
      .catch(() => setFull(programacion))
      .finally(() => setIsLoading(false));
  }, [isOpen, programacion]);

  if (!isOpen || !programacion) return null;

  const data = full ?? programacion;
  const asignaciones = data.asignaciones ?? [];

  const reload = () => {
    if (!programacion) return;
    ProgramacionService.getById(programacion.id)
      .then(setFull)
      .catch(() => undefined);
  };

  return (
    <div className="fixed left-0 lg:left-56 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={`absolute inset-0 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          onClick={onClose}
        />

        <section
          className={`absolute right-0 top-0 h-full w-full max-w-none bg-white border-l border-gray-200 shadow-2xl transform transition-transform duration-300 ease-in-out ${
            isVisible ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-sm font-medium text-gray-900">
                    Programación: {formatearFecha(data.fechaInicio)} — {formatearFecha(data.fechaFin)}
                  </h2>
                  <p className="text-xs text-gray-500">{data.descripcion}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Contenido */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent" />
                </div>
              ) : asignaciones.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-16">
                  Esta programación no tiene asignaciones registradas.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {asignaciones.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setAsignacionSel(a)}
                      className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-purple-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-xs font-medium text-gray-900">{formatearFecha(a.fecha)}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            {diaDeFecha(a.fecha)} · {a.horaInicio} – {a.horaFin}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <EstadoSeguimientoBadge estado={a.estado} />
                          <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5" />
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                        <p className="text-[11px]">
                          <span className="text-gray-400">Participante: </span>
                          <span className="text-gray-700">{a.participante?.nombreCompleto ?? '—'}</span>
                        </p>
                        <p className="text-[11px]">
                          <span className="text-gray-400">Ministerio: </span>
                          <span className="text-gray-700">{a.ministerio?.nombre ?? '—'}</span>
                        </p>
                        <p className="text-[11px]">
                          <span className="text-gray-400">Líder: </span>
                          <span className="text-gray-700">{a.lider?.nombreCompleto ?? '—'}</span>
                        </p>
                        {(a.rol || a.actividad) && (
                          <p className="text-[11px]">
                            <span className="text-gray-400">{a.rol ? 'Rol' : 'Actividad'}: </span>
                            <span className="text-gray-700">{a.rol ?? a.actividad}</span>
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Drawer anidado: detalle de la asignación seleccionada */}
        <AsignacionDetailsDrawer
          isOpen={!!asignacionSel}
          asignacion={asignacionSel}
          onClose={() => setAsignacionSel(null)}
          onEdit={() => {
            setAsignacionSel(null);
            onEdit(data);
          }}
          onSeguimientoActualizado={reload}
        />
      </div>
    </div>
  );
};
